import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import { mdiAirplaneLanding, mdiAirplaneTakeoff } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemStatus } from '../../resources/types';


interface SelectStatusSingleProps {
    single: true; //just true
    value?: number; // Single ID
    width?: number;
    filter?: string;
    onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

// When single = false (or undefined, as per union type)
interface SelectStatusMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    filter?: string;
    onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

export type SelectStatusProps =
    | SelectStatusSingleProps
    | SelectStatusMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectStatus: React.FC<SelectStatusProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        filter = "",
        readonly,
        tokenLimit = 3,
        hideTokens = false,
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.gates) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemStatus | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemStatus[]>([]);

    // --- Mapped Categories (Options List) ---

    const mappedStatuses: ItemStatus[] = useMemo(() => {
        // 1. Define the mapping between your filter parameter and the "tipo" property
        const filterMap: Record<string, string> = {
            "ARR": "STATUS.ARR",
            "DEP": "STATUS.DEP"
        };

        const targetTipo = filterMap[filter];

        return data.statuses
            // 2. Filter the array: only keep items matching the target tipo
            // (If filter is missing or doesn't match ARR/DEP, it shows all)
            .filter((plan: ItemStatus) => !targetTipo || plan.tipo === targetTipo)
            .map((plan: ItemStatus) => ({
                idStatus: plan.idStatus,
                tipo: plan.tipo,
                status: plan.statusES,
                description: plan.statusEN,
                value: plan.idStatus.toString(),
                label: (
                    <>
                        <Icon
                            path={plan.tipo?.endsWith(".ARR") ? mdiAirplaneLanding : mdiAirplaneTakeoff}
                            size={1}
                        />
                        {plan.statusES}
                    </>
                ),
                AODBTAG: plan.AODBTAG
            }))
            .sort((a: { AODBTAG: any; }, b: { AODBTAG: any; }) => (a.AODBTAG || "").localeCompare(b.AODBTAG || ""));
    }, [data.statuses, filter]); // Added 'filter' as a dependency

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedStatuses.find(status => status.idStatus === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedStatuses.filter(status => ids.includes(status.idStatus));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedStatuses]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemStatus | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idStatus ?? null; // Extract ID
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemStatus[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idStatus); // Extract array of IDs
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar estado" : "Seleccionar estados";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedStatuses}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={selectedMulti.length === 0 ? placeholder : `${selectedMulti.length}/${mappedStatuses.length} seleccionadas`}
                    selectedOptions={selectedMulti}
                    options={mappedStatuses}
                    onChange={handleMultiChange}
                />
            )}

        </div>
    );
};

export default SelectStatus;